import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Center,
  Flex,
  Heading,
  SimpleGrid,
  Text,
  VStack,
  useColorMode,
} from "@chakra-ui/react";
import { Chart } from "react-google-charts";
import getSymbolFromCurrency from "currency-symbol-map";
import { getOptionsExpiryPerformance } from "../store/analytics/thunks";
import AnalyticsSkeleton from "./AnalyticsSkeleton";
import DataTable from "./common/DataTable";

export default function OptionsAnalytics({ user }) {
  const dispatch = useDispatch();
  const { colorMode } = useColorMode();
  const optionsExpiry = useSelector((state) => state.analytics.optionsExpiry) || [];
  const loading = useSelector((state) => state.ui.loading);
  const preferences = useSelector((state) => state.user.preferences);
  const currencySymbol =
    getSymbolFromCurrency(preferences?.preferred_currency || "USD") || "$";

  useEffect(() => {
    dispatch(getOptionsExpiryPerformance());
  }, [dispatch]);

  const totalOptionsTrades = optionsExpiry.reduce((acc, b) => acc + (b.num_trades || 0), 0);

  if (loading && totalOptionsTrades === 0) {
    return (
      <Flex flexDirection="column" height="100vh" backgroundColor={colorMode === "light" ? "gray.200" : "gray.800"}>
        <Box flex="auto" overflowX="auto" overflowY="auto">
          <VStack spacing={4} p={4} align="stretch" w="full">
            <AnalyticsSkeleton heatmapColumns={{ base: 2, sm: 3, md: 4 }} heatmapCount={4} />
          </VStack>
        </Box>
      </Flex>
    );
  }

  if (totalOptionsTrades === 0) {
    return (
      <Flex flexDirection="column" height="100vh" backgroundColor={colorMode === "light" ? "gray.200" : "gray.800"}>
        <Box flex="auto" overflowX="auto" overflowY="auto">
          <Center h="60vh">
            <Text color={colorMode === "light" ? "gray.500" : "gray.400"}>
              No options trades with expiry data yet — log options trades to see DTE buckets.
            </Text>
          </Center>
        </Box>
      </Flex>
    );
  }

  const chartData = [
    ["Bucket", "Win %", { role: "style" }],
    ...optionsExpiry.map((b) => [
      b.bucket,
      b.win_pct ?? 0,
      (b.win_pct ?? 0) >= 50 ? "#48BB78" : "#FC8181",
    ]),
  ];

  const chartOptions = {
    title: "Win Rate by DTE Bucket",
    chartArea: { width: "70%", height: "75%" },
    backgroundColor: colorMode === "light" ? "#FDFDFD" : "#1A202C",
    titleTextStyle: { color: colorMode === "light" ? "#636363" : "#E2E8F0", bold: true, fontSize: 16 },
    legend: "none",
    hAxis: {
      title: "Win Rate %",
      titleTextStyle: { color: colorMode === "light" ? "#636363" : "#E2E8F0" },
      textStyle: { color: colorMode === "light" ? "#636363" : "#E2E8F0" },
    },
    vAxis: { textStyle: { color: colorMode === "light" ? "#636363" : "#E2E8F0" } },
  };

  return (
    <Flex flexDirection="column" height="100vh" backgroundColor={colorMode === "light" ? "gray.200" : "gray.800"}>
      <Box flex="auto" overflowX="auto" overflowY="auto">
        <VStack spacing={4} p={4} align="stretch" w="full">
          <Heading className="page-heading" fontSize="md" color={colorMode === "light" ? "gray.600" : "gray.200"}>
            Options Analytics
          </Heading>

          <SimpleGrid columns={{ base: 2, sm: 4 }} spacing={3}>
            {optionsExpiry.map((b) => (
              <Box
                key={b.bucket}
                bg={colorMode === "light" ? "white" : "gray.700"}
                rounded="md"
                p={4}
                boxShadow="sm"
              >
                <Text fontSize="xs" color={colorMode === "light" ? "gray.500" : "gray.400"}>
                  {b.bucket}
                </Text>
                <Text fontSize="lg" fontWeight="bold">
                  {b.win_pct != null ? `${b.win_pct}% WR` : "—"}
                </Text>
                <Text
                  fontSize="sm"
                  color={b.total_pnl >= 0 ? "green.400" : "red.400"}
                >
                  {b.total_pnl >= 0 ? "+" : ""}{currencySymbol}{Number(b.total_pnl).toFixed(2)}
                </Text>
                <Text fontSize="xs" color={colorMode === "light" ? "gray.500" : "gray.400"}>
                  {b.num_trades} trade{b.num_trades === 1 ? "" : "s"}
                </Text>
              </Box>
            ))}
          </SimpleGrid>

          <Box bg={colorMode === "light" ? "white" : "gray.700"} rounded="lg" p={4} h="360px">
            <Chart chartType="BarChart" width="100%" height="100%" data={chartData} options={chartOptions} />
          </Box>

          <Box>
            <Heading size="sm" mb={3} color={colorMode === "light" ? "gray.600" : "gray.200"}>
              Breakdown
            </Heading>
            <DataTable
              columns={[
                { key: "bucket", header: "Bucket" },
                { key: "num_trades", header: "Trades" },
                { key: "num_wins", header: "Wins" },
                { key: "num_losses", header: "Losses" },
                { key: "win_pct", header: "Win Rate %", render: (row) => row.win_pct != null ? `${row.win_pct}%` : "—" },
                {
                  key: "total_pnl",
                  header: "Total P&L",
                  render: (row) =>
                    `${row.total_pnl >= 0 ? "+" : ""}${currencySymbol}${Number(row.total_pnl).toFixed(2)}`,
                },
                {
                  key: "avg_pnl",
                  header: "Avg P&L",
                  render: (row) =>
                    row.avg_pnl != null
                      ? `${row.avg_pnl >= 0 ? "+" : ""}${currencySymbol}${Number(row.avg_pnl).toFixed(2)}`
                      : "—",
                },
              ]}
              data={optionsExpiry}
            />
          </Box>
        </VStack>
      </Box>
    </Flex>
  );
}
